import React, { useState, useEffect } from 'react';
import { post, get } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import './PushNotifications.css';

const PushNotifications = () => {
  const [devices, setDevices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const { getAuthToken } = useAuth();

  // Filters
  const [filterType, setFilterType] = useState('all');
  const [environmentFilter, setEnvironmentFilter] = useState('all');

  // Notification form
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [targetType, setTargetType] = useState('all');
  const [targetEnvironment, setTargetEnvironment] = useState('all');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchDevices();
  }, [filterType, environmentFilter]);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: `/v1/notifications/devices?filter=${filterType}&environment=${environmentFilter}&limit=100`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setDevices(data.devices || []);
      setSummary(data.summary || null);
    } catch (err) {
      console.error('Error fetching devices:', err);
      setError('Failed to fetch devices');
    } finally {
      setLoading(false);
    }
  };

  const sendNotification = async (e) => {
    e.preventDefault();

    if (!notificationTitle.trim() || !notificationMessage.trim()) {
      setError('Please enter both title and message');
      return;
    }

    try {
      setSending(true);
      setError(null);
      setSuccessMessage(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/notifications/send-push-bulk',
        options: {
          headers: {
            Authorization: token,
          },
          body: {
            title: notificationTitle.trim(),
            message: notificationMessage.trim(),
            target: targetType,
            environment_filter: targetEnvironment,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setSuccessMessage(
        `${data.message}\nSuccessfully sent to ${data.success_count}/${data.devices_targeted} devices`
      );
      setNotificationTitle('');
      setNotificationMessage('');

      // Refresh devices list
      await fetchDevices();
    } catch (err) {
      console.error('Error sending notification:', err);
      setError(err.message || 'Failed to send notification. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffMins < 1440) {
      return `${Math.floor(diffMins / 60)}h ago`;
    } else {
      return `${Math.floor(diffMins / 1440)}d ago`;
    }
  };

  return (
    <div className="push-notifications-container">
      <div className="page-header">
        <h1>📱 Push Notifications Control Panel</h1>
        <p>Manage and send push notifications to devices</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {successMessage && (
        <div className="alert alert-success" style={{ whiteSpace: 'pre-line' }}>
          {successMessage}
        </div>
      )}

      {/* Stats Section */}
      {summary && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📱</div>
            <div className="stat-value">{summary.total}</div>
            <div className="stat-label">Total Devices</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">👤</div>
            <div className="stat-value">{summary.registered}</div>
            <div className="stat-label">Registered</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">👻</div>
            <div className="stat-value">{summary.anonymous}</div>
            <div className="stat-label">Anonymous</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🧪</div>
            <div className="stat-value">{summary.by_environment.sandbox}</div>
            <div className="stat-label">Sandbox</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-value">{summary.by_environment.production}</div>
            <div className="stat-label">Production</div>
          </div>
        </div>
      )}

      {/* Send Notification Form */}
      <div className="card">
        <h2>Send Push Notification</h2>
        <form onSubmit={sendNotification} className="notification-form">
          <div className="form-group">
            <label htmlFor="title">Title</label>
            <input
              type="text"
              id="title"
              value={notificationTitle}
              onChange={(e) => setNotificationTitle(e.target.value)}
              placeholder="Notification title"
              disabled={sending}
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              value={notificationMessage}
              onChange={(e) => setNotificationMessage(e.target.value)}
              placeholder="Notification message"
              rows="4"
              disabled={sending}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="targetType">Target</label>
              <select
                id="targetType"
                value={targetType}
                onChange={(e) => setTargetType(e.target.value)}
                disabled={sending}
              >
                <option value="all">All Devices</option>
                <option value="registered">Registered Users Only</option>
                <option value="anonymous">Anonymous Devices Only</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="targetEnvironment">Environment</label>
              <select
                id="targetEnvironment"
                value={targetEnvironment}
                onChange={(e) => setTargetEnvironment(e.target.value)}
                disabled={sending}
              >
                <option value="all">All Environments</option>
                <option value="sandbox">Sandbox Only (TestFlight)</option>
                <option value="production">Production Only (App Store)</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={sending || !notificationTitle.trim() || !notificationMessage.trim()}
          >
            {sending ? 'Sending...' : '📤 Send Notification'}
          </button>
        </form>
      </div>

      {/* Filters and Device List */}
      <div className="card">
        <div className="card-header">
          <h2>Registered Devices ({devices.length})</h2>
          <button onClick={fetchDevices} className="btn btn-secondary" disabled={loading}>
            {loading ? '⏳' : '🔄'} Refresh
          </button>
        </div>

        <div className="filters">
          <div className="form-group">
            <label htmlFor="filterType">Registration Status</label>
            <select
              id="filterType"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              disabled={loading}
            >
              <option value="all">All Devices</option>
              <option value="registered">Registered Only</option>
              <option value="anonymous">Anonymous Only</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="environmentFilter">Environment</label>
            <select
              id="environmentFilter"
              value={environmentFilter}
              onChange={(e) => setEnvironmentFilter(e.target.value)}
              disabled={loading}
            >
              <option value="all">All Environments</option>
              <option value="sandbox">Sandbox</option>
              <option value="production">Production</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading-state">Loading devices...</div>
        ) : devices.length === 0 ? (
          <div className="empty-state">No devices found matching the filters</div>
        ) : (
          <div className="devices-list">
            {devices.map((device) => (
              <div key={device.device_token} className="device-card">
                <div className="device-header">
                  <div className="device-info">
                    <span className="device-icon">📱</span>
                    <div>
                      <div className="device-user">
                        {device.user_id || 'Anonymous User'}
                      </div>
                      <div className="device-token">
                        Token: {device.device_token.substring(0, 16)}...
                      </div>
                    </div>
                  </div>
                  <span
                    className={`badge badge-${
                      device.environment === 'sandbox' ? 'warning' : 'success'
                    }`}
                  >
                    {device.environment.toUpperCase()}
                  </span>
                </div>
                <div className="device-meta">
                  <span>Platform: {device.platform}</span>
                  <span>Updated: {formatTimestamp(device.updated_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PushNotifications;
